/**
 * The Business Unit deactivate confirmation (f5.1), driven entirely by the server.
 *
 * The prototype's `modal narrow` with `alert alert-warning` carrying `a-title`
 * and `a-body`, plus its Reason textarea, and its exact promise: *"The unit
 * stops appearing in new cycles and its Input Sheet is no longer generated.
 * Committed history is kept and the unit can be reactivated."*
 *
 * **The client never computes the child list.** The sequence is f4.1's, and it
 * matters more here than it looks:
 *
 * 1. The dialog opens as a plain confirmation.
 * 2. Confirming issues the deactivation **without** `acknowledge_active_children`.
 * 3. If the server refuses with `409 ACTIVE_CHILDREN_PRESENT`, the dialog stays
 *    open and renders the children **it** named.
 * 4. Confirming again re-issues the same request with the acknowledgement.
 *
 * A Business Unit has no registered children until f6.1 adds `Client`, so step
 * 3 never fires today. That is exactly why the flow is built this way now:
 * when f6.1 registers its descriptor, this dialog starts listing clients with
 * **no change to this file** -- and a client created between steps 2 and 4
 * makes step 4 refuse again rather than succeed silently.
 */

import { useState } from 'react';
import { Ban, TriangleAlert } from 'lucide-react';

import type { BusinessUnit } from '@/api/client';
import { useDeactivateBusinessUnit } from '@/api/queries';
import { Modal } from '@/routes/reference-data/Modal';

import { activeChildrenFrom, generalMessage } from './unitView';

export function DeactivateUnitDialog({
  unit,
  onClose,
}: {
  unit: BusinessUnit;
  onClose: () => void;
}) {
  const deactivate = useDeactivateBusinessUnit();
  const [reason, setReason] = useState('');

  const activeChildren = activeChildrenFrom(deactivate.error);
  // A refusal that named children is the expected answer to step 2, not a
  // failure to repeat at the top of the dialog.
  const failure = activeChildren ? null : generalMessage(deactivate.error);

  function submit() {
    deactivate.mutate(
      {
        unitId: unit.id,
        body: {
          // False on the first attempt, true once the warning has been seen.
          acknowledge_active_children: activeChildren !== null,
          ...(reason.trim() ? { reason: reason.trim() } : {}),
        },
      },
      { onSuccess: onClose },
    );
  }

  return (
    <Modal
      title="Deactivate Business Unit?"
      subtitle={unit.name}
      onClose={onClose}
      onSubmit={submit}
      busy={deactivate.isPending}
      submitLabel={activeChildren ? 'Deactivate anyway' : 'Deactivate'}
      submitIcon={<Ban aria-hidden="true" />}
      submitClassName="btn btn-danger"
      narrow
      layout="plain"
      message={failure}
    >
      <div className="alert alert-warning mb-4" role="alert">
        <TriangleAlert aria-hidden="true" />
        <div>
          <div className="a-title">
            {activeChildren && activeChildren.total > 0
              ? `${activeChildren.total} ${
                  activeChildren.total === 1 ? 'record is' : 'records are'
                } still active under this unit`
              : `${unit.name} will stop appearing in new cycles`}
          </div>
          <div className="a-body">
            The unit stops appearing in new cycles and its Input Sheet is no longer
            generated. Committed history is kept and the unit can be reactivated.
          </div>
          {activeChildren && activeChildren.listed.length > 0 && (
            <>
              <ul className="group-children">
                {activeChildren.listed.map((entry) => (
                  <li key={entry.id}>
                    <span className="mono">{entry.code}</span> {entry.name}
                  </li>
                ))}
              </ul>
              {activeChildren.total > activeChildren.listed.length && (
                <div className="a-body">
                  and {activeChildren.total - activeChildren.listed.length} more.
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <div className="field">
        <label htmlFor="bu-deact-reason">Reason</label>
        <textarea
          id="bu-deact-reason"
          value={reason}
          placeholder="Recorded on the audit entry"
          onChange={(event) => setReason(event.target.value)}
        />
      </div>
    </Modal>
  );
}
